import React, { useCallback, useMemo, useState } from 'react';
import { AppButton, Box, ErrorModal, Preloader, Text } from '@common-fe/common-fe';

import { ROUTES } from '@/common';
import { SERVER_ERROR_MODAL_TEXT, SERVER_ERROR_MODAL_TITLE } from '@/common/constants';
import { Modal, PreventLeaveModal } from '@/components';
import { SuccessModal } from '@/components/elements';
import { useHistory } from '@/modules/core/hooks';
import useCreateFieldLevelAccessQuery from '@/modules/role/queries/useCreateFieldLevelAccess.query';
import { IFieldLevelAccessSetup, RolesAndSecurityTabs } from '@/modules/role/role.types';
import { useRolesAndSecurityTabsStore } from '@/modules/role/stores';

import FieldLevelAccessSetupForm from '../FieldLevelAccessSetupForm';
import { useFieldLevelAccessSetupStore } from '../stores';

interface Props {
  isDirty?: boolean;
  visible: boolean;
  isEditMode?: boolean;
  onSetVisible: (value: boolean) => void;
}
const FieldLevelAccessSetupConfirmModal: React.FC<Props> = ({
  isDirty,
  isEditMode,
  visible,
  onSetVisible
}) => {
  const state = useFieldLevelAccessSetupStore((state) => state.state);
  const [isServerError, setIsServerError] = useState(false);
  const handleReset = useFieldLevelAccessSetupStore((state) => state.handleReset);
  const [isPrevent, setIsPrevent] = useState(true);
  const [successModal, setSuccessModal] = useState(false);

  const handleToggleVisible = useCallback(() => {
    onSetVisible(!visible);
  }, [onSetVisible, visible]);

  const setCurrentTab = useRolesAndSecurityTabsStore((state) => state.setCurrentTab);
  const setState = useFieldLevelAccessSetupStore((state) => state.setState);
  const history = useHistory();
  const handleSubmit = useCallback(
    (data: IFieldLevelAccessSetup) => {
      setState(data);
      history.push(ROUTES.ROLE_SETUP);
    },
    [history, setState]
  );

  const formattedData: IFieldLevelAccessSetup = useMemo(() => {
    return {
      ...(state as IFieldLevelAccessSetup),
    };
  }, [state]);
  const { handleSave, isLoading } = useCreateFieldLevelAccessQuery({
    onClose: () => {
      onSetVisible(false);
    },
    onError: () => {
      setIsServerError(true);
    },
    isEditMode,
  });

  const handleConfirm = useCallback(async () => {
    try {
      setIsServerError(false);
      await handleSave(formattedData as IFieldLevelAccessSetup);
      setSuccessModal(true);
      onSetVisible(false);
      setIsPrevent(false);
    } catch {
    // 
    }
  }, [formattedData, handleSave, onSetVisible]);
  
  const handleClose = useCallback(() => {
    setIsPrevent(false);
    setSuccessModal(false);
    handleReset();
    setCurrentTab(RolesAndSecurityTabs.FIELD_LEVEL_ACCESS);
    history.push(ROUTES.ROLES_AND_SECURITY);
  }, [handleReset, history, setCurrentTab]);
  return (
    <>
      <PreventLeaveModal
        subText="By leaving now, your progress will not be saved. "
        isPrevent={!!isDirty && !!isPrevent}
      />
      <SuccessModal
        testId='field-level-access-setup_success'
        visible={successModal}
        header={`${state.name} has been saved!`}
        buttonText="Go To Field Level Access"
        onSetVisible={handleClose}
      />
      <Box style={{zIndex: 10000}}>
        <ErrorModal
          testId="FieldLevelAccessSetupConfirmModal_server-error"
          visible={isServerError}
          header={SERVER_ERROR_MODAL_TITLE}
          helpText={SERVER_ERROR_MODAL_TEXT}
          buttonText="Close"
          
          buttonTextTryAgain="Try again"
          onSetVisible={setIsServerError}
          onSubmit={handleConfirm}
        
        />
      </Box>
      <Modal testId='field-level-access-setup_confirm' visible={visible} onSetVisible={onSetVisible} wrapperStyle={{ zIndex: 1003 }}>
        <FieldLevelAccessSetupForm
          isModalMode
          isViewMode
          title={(
            <Text size="2xl" weight={700} margin={{ bottom: 'l' }} style={{ lineHeight: '32px' }}>
              Save {state.name}
            </Text>
          )}
          onSubmit={handleSubmit}
          defaultValues={formattedData}
          footer={(
            <Box margin={{ top: 'l' }} direction="row" justify="end">
              <AppButton
                testId="field-level-access-confirm_cancel"
                width="160px"
                buttonType="secondary"
                onClick={handleToggleVisible}
              >
                Cancel
              </AppButton>
              <Box margin={{ left: 's' }}>
                <AppButton
                  testId="field-level-access-confirm_confirm"
                  disabled={isLoading}
                  width="160px"
                  onClick={handleConfirm}
                  buttonType="primary"
                >
                  {isLoading ? <Preloader /> : 'Confirm'}
                </AppButton>
              </Box>
            </Box>
          )}
        >
        </FieldLevelAccessSetupForm>
      </Modal>
    </>
  );
};

export default FieldLevelAccessSetupConfirmModal;
